import {useEffect, useState, useMemo, useCallback} from "react";
import {Navigate, Route, Routes, useLocation, useNavigate} from "react-router-dom";
import {ThemeProvider} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Sidenav from "./custom/Sidenav";
import theme from "assets/theme";
import routes from "routes";
import {setAuthentication, setLoggedInUserDetail, setMiniSidenav, useRayzayUIController} from "context";
import logo from "assets/images/logo.png";
import useAuthGuard from "./service/auth/authGuard";
import RoleBasedRoute from "./service/auth/RoleBasedRoute";
import MaintenancePage from "./layouts/maintenance/MaintenancePage";
import useBackendHealth from "./hooks/useBackendHealth";
import {clearAllAuthData} from "./api/auth/authApiClient";
import LoadingSpinner from "./components/LoadingSpinner";

export default function App() {
  const [controller, dispatch] = useRayzayUIController();
    const {miniSidenav, direction, layout, sidenavColor} = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();
    const {loggedInUserDetail} = controller;
    const location = useLocation();
    const navigate = useNavigate();
    const [isAuthChecking, setIsAuthChecking] = useState(true);

    // Backend health check
    const {isSystemHealthy, isChecking, hasCheckedOnce, performHealthCheck} = useBackendHealth();

    // Only run auth guard, skip token validation to prevent race conditions
  useAuthGuard();
    // useTokenValidation(); // Commented out to prevent race conditions

    // Global error handler for authentication failures
    useEffect(() => {
        const handleGlobalError = (event) => {
            if (event.error && event.error.response) {
                const status = event.error.response.status;
                if (status === 401) {
                    console.log(`Authentication error (${status}) detected, redirecting to sign-in`);
                    clearAllAuthData();
                    setAuthentication(dispatch, false);
                    setLoggedInUserDetail(dispatch, null);
                    if (!location.pathname.includes('/authentication/sign-in')) {
                        navigate('/authentication/sign-in');
                    }
                }
                // Don't automatically redirect on 403 errors as they might be resource-specific
                // Let the individual components handle 403 errors appropriately
            }
        };

        window.addEventListener('unhandledrejection', handleGlobalError);

        return () => {
            window.removeEventListener('unhandledrejection', handleGlobalError);
        };
    }, [dispatch, location.pathname, navigate]);

    // Set auth checking to false after initial load
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsAuthChecking(false);
        }, 1000); // Give auth guard time to initialize

        return () => clearTimeout(timer);
    }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

    const getRoutes = useCallback((allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

        if (route.children) {
            // Handle nested routes
            return (
                <Route key={route.key} path={route.route} element={
                    route.roles ? (
                        <RoleBasedRoute
                            userRole={loggedInUserDetail?.userRole}
                            allowedRoles={route.roles}>
                            {route.component}
                        </RoleBasedRoute>
                    ) : (route.component)
                }>
                    {getRoutes(route.children)}
                </Route>
            );
        }

      if (route.route) {
        return <Route exact path={route.route}
                      key={route.key}
                      element={route.roles ? (
                          <RoleBasedRoute
                              userRole={loggedInUserDetail?.userRole}
                              allowedRoles={route.roles}>
                            {route.component}
                          </RoleBasedRoute>
                      ) : (route.component)}/>;
      }
      return null;
    }), [loggedInUserDetail?.userRole]);

    // When backend comes back up, attempt to restore authentication if we have valid tokens
    useEffect(() => {
        if (isSystemHealthy && !controller.isAuthenticated) {
            const accessToken = localStorage.getItem(process.env.REACT_APP_AUTH_TOKEN_KEY || 'token');
            const refreshToken = localStorage.getItem(process.env.REACT_APP_REFRESH_TOKEN_KEY || 'refreshToken');

            if (accessToken && refreshToken) {
                console.log('Backend is back up, attempting to restore authentication');
            }
        }
    }, [isSystemHealthy, controller.isAuthenticated]);

    // Add immediate health check when maintenance page should be shown
    useEffect(() => {
        if (!isSystemHealthy && pathname !== "/maintenance") {
            const immediateCheck = setTimeout(() => {
                console.log('Performing immediate health check from maintenance page');
                performHealthCheck();
            }, 2000);

            return () => {
                clearTimeout(immediateCheck);
            }
        }
    }, [isSystemHealthy, pathname, performHealthCheck]);

    // Memoize the app state to prevent unnecessary re-renders
    const appState = useMemo(() => ({
        shouldShowMaintenance: !isSystemHealthy && hasCheckedOnce && pathname !== "/maintenance",
        shouldRedirectFromMaintenance: isSystemHealthy && hasCheckedOnce && pathname === "/maintenance",
        shouldShowLoading: !hasCheckedOnce && isChecking,
        shouldShowAuthLoading: isAuthChecking && !controller.isAuthenticated && !pathname.includes('/authentication/') && pathname !== '/maintenance' && pathname !== '/' && pathname !== '/dashboard'
    }), [isSystemHealthy, hasCheckedOnce, pathname, isChecking, isAuthChecking, controller.isAuthenticated]);

    console.log('App.js - Backend health status:', {
        isSystemHealthy,
        isChecking,
        hasCheckedOnce,
        pathname,
        shouldShowMaintenance: appState.shouldShowMaintenance
    });

    // Show maintenance page only if backend is down, we've checked at least once, and we're not already on maintenance page
    if (appState.shouldShowMaintenance) {
        console.log('Redirecting to maintenance page - backend is down'); // Debug log
        return (
            <ThemeProvider theme={theme}>
                <CssBaseline/>
                <MaintenancePage
                    onRetry={performHealthCheck}
                    isLoading={isChecking}
                />
            </ThemeProvider>
        );
    }

    // If we're on maintenance page but backend is healthy, redirect to dashboard
    if (appState.shouldRedirectFromMaintenance) {
        console.log('Backend is healthy, redirecting from maintenance page to dashboard'); // Debug log
        navigate('/dashboard');
        return null;
    }

    // Show loading while initial health check is happening
    if (appState.shouldShowLoading) {
        console.log('Showing loading while initial health check...'); // Debug log
        return (
            <ThemeProvider theme={theme}>
                <CssBaseline/>
                <LoadingSpinner message="Checking system status..." showSidenav={false}/>
            </ThemeProvider>
        );
    }

    // Show loading while authentication is being checked
    if (appState.shouldShowAuthLoading) {
        console.log('Showing loading while checking authentication...'); // Debug log
        return (
            <ThemeProvider theme={theme}>
                <CssBaseline/>
                <LoadingSpinner message="Checking authentication..." showSidenav={false}/>
            </ThemeProvider>
        );
    }

    console.log('Rendering normal app - backend is healthy'); // Debug log

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={logo}
            brandName="Mytrustie"
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
        </>
      )}
      <Routes>
        {getRoutes(routes)}
          <Route path="/" element={<Navigate to="/dashboard"/>}/>
          <Route path="/maintenance" element={<MaintenancePage onRetry={performHealthCheck} isLoading={isChecking}/>}/>
          <Route path="*" element={<Navigate to="/dashboard"/>}/>
      </Routes>
    </ThemeProvider>
  );
}
